import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useReportTabDataQuery } from '@/features/reports/api/useReportsQueries';
import { REPORT_TABS, type ReportTabKey } from './reportDetailPage.constants';
import { Loading } from './ReportDetailPagePrimitives';
import { AnalyticsTab } from './tabs/AnalyticsTab';
import { ProgressTab } from './tabs/ProgressTab';
import { CommentsTab } from './tabs/CommentsTab';

export function ReportDetailPage() {
  const { id: token } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as ReportTabKey) || 'analytics';
  const setTab = (t: ReportTabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  const tabDataQ = useReportTabDataQuery(token, tab);
  const data = tabDataQ.data?.data;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/reports')}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-[var(--dash-line)] bg-white text-[var(--dash-muted)] shadow-sm transition hover:text-[var(--dash-brand)] hover:shadow-md"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="text-left">
          <h1 className="m-0 text-xl font-bold tracking-tight text-[var(--dash-ink)]">
            {data?.campaign?.campaignName || 'Shared Dashboard'}
          </h1>
          <p className="m-0 mt-0.5 text-xs text-[var(--dash-muted)]">
            Campaign reports &amp; tracking
          </p>
        </div>
      </div>

      <nav className="flex gap-1.5 overflow-x-auto rounded-2xl border border-[var(--dash-line)] bg-white p-1.5 shadow-sm">
        {REPORT_TABS.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2.5 text-[13px] font-semibold whitespace-nowrap transition ${
                active
                  ? 'bg-[var(--dash-brand)] text-white shadow-sm'
                  : 'text-[var(--dash-muted)] hover:bg-[var(--dash-soft)] hover:text-[var(--dash-ink)]'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </nav>

      {tabDataQ.isPending ? (
        <Loading />
      ) : (
        <>
          {tab === 'analytics' ? <AnalyticsTab data={data} /> : null}
          {tab === 'progress' ? <ProgressTab token={token} data={data} /> : null}
          {tab === 'comments' ? <CommentsTab token={token} data={data} /> : null}
        </>
      )}
    </div>
  );
}
